import * as api from '@gitbook/api-client';
import type { Orderable } from '@gitbook/util-orderindex';
import { arrayOfAll } from '@gitbook/util-ts';
//
// Codes
//
export const BLOCK_CODE = 'code';
export const BLOCK_CODE_LINE = 'code-line';

export type BlockCodeData = api.DocumentBlockCode['data'];

/**
 * Constant that defines the default mode for overflow in a code block.
 */
export const BLOCK_CODE_DEFAULT_OVERFLOW = 'scroll';

export interface BlockCodeLineData {
    highlighted?: boolean;
}

/**
 * Columns - column layout block.
 */
export const BLOCK_COLUMNS: api.DocumentBlockColumns['type'] = 'columns';
export type BlockColumnsData = api.DocumentBlockColumns['data'];

export const BLOCK_COLUMN: api.DocumentBlockColumn['type'] = 'column';
export type BlockColumnData = api.DocumentBlockColumn['data'];

//
// Embeds
//
export const BLOCK_EMBED = 'embed';

export interface BlockEmbedData {
    url: string;
    fullWidth?: boolean;
}

//
// Drawing
//
export const BLOCK_DRAWING = 'drawing';

export interface BlockDrawingData {
    ref?: api.ContentRefFile;
}

//
// Files
//
export const BLOCK_FILE = 'file';

export interface BlockFileData {
    ref: api.ContentRefFile;
}

//
// Images
//
export const BLOCK_IMAGES = 'images';
export const BLOCK_IMAGE = 'image';

export const FRAGMENT_CAPTION = 'caption';

export type BlockImagesAlign = 'center' | 'left' | 'right';
export interface BlockImagesData {
    align?: BlockImagesAlign;
    fullWidth?: boolean;
    withFrame?: boolean;
}

export interface BlockImageData {
    ref: api.ContentRefURL | api.ContentRefFile;

    /** Alternative source ot use when the theme is dark */
    refDark?: api.ContentRefURL | api.ContentRefFile;

    width?: BlockImageSizeValue;
    height?: BlockImageSizeValue;

    /** Alternative text to display when the image is not available. */
    alt?: string;
}

/**
 * Represents a value for the size of a block image, including both the numerical value and the unit of measurement (e.g. "px" or "em").
 */
export type BlockImageSizeValue = number | { value: number; unit: string };

/**
 * Constant that defines the default size for inline images if omitted.
 */
export const INLINE_IMAGE_DEFAULT_SIZE = 'original';

//
// Headings
//
export const BLOCK_HEADING_1 = 'heading-1';
export const BLOCK_HEADING_2 = 'heading-2';
export const BLOCK_HEADING_3 = 'heading-3';

export const BLOCK_HEADINGS_ARRAY = [BLOCK_HEADING_1, BLOCK_HEADING_2, BLOCK_HEADING_3] as const;
export const BLOCK_HEADINGS: Set<string> = new Set(BLOCK_HEADINGS_ARRAY);
export type BLOCK_HEADING_TYPES = (typeof BLOCK_HEADINGS_ARRAY)[number];

export type BlockHeadingData = api.DocumentBlockHeading['data'];

export type BlockParagraphData = Exclude<api.DocumentBlockParagraph['data'], undefined>;

//
// Hints
//
export const BLOCK_HINT = 'hint';

export type BlockHintStyle = 'info' | 'warning' | 'danger' | 'success';
export interface BlockHintData {
    style: BlockHintStyle;
    icon?: string;
}

//
// Prompts
//
export const BLOCK_PROMPT = 'prompt';

export type BlockPromptData = api.DocumentBlockPrompt['data'];

//
// Lists
//
export const BLOCK_LIST_UL = 'list-unordered';
export const BLOCK_LIST_OL = 'list-ordered';
export const BLOCK_LIST_TASKS = 'list-tasks';
export const BLOCK_LIST_ITEM = 'list-item';

export interface BlockOrderedListData {
    /**
     * An integer to start counting from for the list items.
     */
    start?: number;
}

export interface BlockTaskListItemData {
    checked: boolean;
}

export type BlockListItemData = BlockTaskListItemData | Partial<BlockTaskListItemData>;

//
// Paragraphs
//
export const BLOCK_PARAGRAPH = 'paragraph';

//
// Dividers
//
export const BLOCK_DIVIDER = 'divider';

//
// Quotes
//
export const BLOCK_QUOTE = 'blockquote';

//
// Conditional block
//
export const BLOCK_IF = 'if';
export interface BlockIfData {
    expression: string;
}

//
// OpenAPI
//

/**
 * Legacy OpenAPI block that supports file and URL ref.
 * It's named swagger for historical reason.
 * Replaced by BLOCK_OPENAPI_OPERATION but keep for backward compatibility.
 */
export const BLOCK_OPENAPI = 'swagger';

/**
 * Valid content references for schema-based OpenAPI blocks.
 */
export type BlockOpenAPIContentReference = api.ContentRefURL | api.ContentRefFile;

/**
 * API Blocks backed by an OpenAPI schema.
 */
export interface BlockOpenAPIData {
    ref: BlockOpenAPIContentReference;
    path: string;
    method: string;
    expanded?: boolean;
    fullWidth?: boolean;
}

export const BLOCK_OPENAPI_OPERATION = 'openapi-operation';

/**
 * Data of an OpenAPI operation block.
 */
export interface BlockOpenAPIOperationData {
    ref: api.ContentRefOpenAPI;
    path: string;
    method: string;
    headless?: boolean;
}

export const BLOCK_OPENAPI_SCHEMAS = 'openapi-schemas';

/**
 * Data of an OpenAPI Schemas block.
 */
export interface BlockOpenAPISchemasData {
    ref: api.ContentRefOpenAPI;
    schemas: string[];
    /**
     * Whether to group schemas
     * @default true
     */
    grouped?: boolean;
    /**
     * Custom title for the schemas block.
     * When not provided, defaults to "The <SchemaName> object".
     */
    title?: string;
}

export const BLOCK_OPENAPI_WEBHOOK = 'openapi-webhook';

/**
 * Data of an OpenAPI Webhook block.
 */
export interface BlockOpenAPIWebhookData {
    ref: api.ContentRefOpenAPI;
    name: string;
    method: string;
    headless?: boolean;
}

// Math
//
export const BLOCK_MATH = 'math';
export const INLINE_MATH = 'inline-math';

export interface BlockMathData {
    formula: string;
}

export type InlineMathData = BlockMathData;

//
// Tabs
//
export const BLOCK_TABS = 'tabs';
export interface BlockTabsData {
    fullWidth?: boolean;
}

export const BLOCK_TABS_ITEM = 'tabs-item';
export interface BlockTabItemData {
    title?: string;
    icon?: string;
}

//
// Stepper
//
export const BLOCK_STEPPER: api.DocumentBlockStepper['type'] = 'stepper';
export const BLOCK_STEPPER_STEP: api.DocumentBlockStepperStep['type'] = 'stepper-step';

export type BlockStepperStepData = api.DocumentBlockStepperStep['data'];

//
// Updates
//
export const BLOCK_UPDATES: api.DocumentBlockUpdates['type'] = 'updates';
export const BLOCK_UPDATE: api.DocumentBlockUpdate['type'] = 'update';

export const BLOCK_UPDATES_FORMATS = arrayOfAll<BlockUpdatesDateFormat>()([
    'full',
    'short',
    'numeric',
]);
export type BlockUpdatesDateFormat = NonNullable<
    NonNullable<api.DocumentBlockUpdates['data']>['format']
>;
export type BlockUpdatesData = NonNullable<api.DocumentBlockUpdates['data']>;
export type BlockUpdateData = api.DocumentBlockUpdate['data'];

//
// Expandable blocks
//
export const BLOCK_EXPANDABLE = 'expandable';
export const FRAGMENT_EXPANDABLE_TITLE = 'expandable-title';
export const FRAGMENT_EXPANDABLE_BODY = 'expandable-body';

export type BlockExpandableData = api.DocumentBlockExpandable['data'];

//
// Tables
//
export const BLOCK_TABLE = 'table';
export const FRAGMENT_TABLE_CELL = 'table-cell';

/**
 * Possibly value for a TableRecord.
 */
export type TableRecordValue =
    | string
    | number
    | Array<string>
    | boolean
    | api.ContentRef
    | TableDefinitionImageRecord
    | null;

/**
 * Definition of a record in a table.
 */
export interface TableRecord extends Orderable {
    values: {
        [column: string]: TableRecordValue;
    };
}

/** Definition of a Text column. */
export type TextColumnDefinition = api.DocumentTableDefinitionText;

export type TableTextColumnVerticalAlignment = NonNullable<
    TextColumnDefinition['verticalAlignment']
>;

/** Definition of a Number column. */
export type NumberColumnDefinition = api.DocumentTableDefinitionNumber;

/** Definition of a Checkbox column. */
export type CheckboxColumnDefinition = api.DocumentTableDefinitionCheckbox;

/** Definition of a Files column. */
export type FilesColumnDefinition = api.DocumentTableDefinitionFiles;

/** Definition of a Users column. */
export type UsersColumnDefinition = api.DocumentTableDefinitionUsers;

/** Definition of a Rating column. */
export type RatingColumnDefinition = api.DocumentTableDefinitionRating;

/** Definition of a Select column. */
export type SelectColumnDefinition = api.DocumentTableDefinitionSelect;

/** Option in the table select */
export type SelectColumnOption = api.DocumentTableSelectOption;

/** Definition of a Content ref column. */
export type ContentRefColumnDefinition = api.DocumentTableDefinitionContentRef;

/** Definition of a column in a table. */
export type TableColumnDefinition = api.DocumentTableDefinition;

/** Definition of an Image column. */
export type ImageColumnDefinition = api.DocumentTableDefinitionImage;

/** Image record value that supports both a direct ContentRef or an object with ref and record-level settings (e.g. objectFit) */
export type TableDefinitionImageRecord = api.DocumentTableImageRecord;

/** Column types for tables. */
export type TableColumnType = TableColumnDefinition['type'];

/** GridView for table data. */
export type TableGridView = api.DocumentTableViewGrid;

/**
 * View for table data as cards.
 */
export type TableCardsView = api.DocumentTableViewCards;

/** Definition of a Table view. */
export type TableView = TableGridView | TableCardsView;

/** Definition of Table views. */
export type TableViewType = TableView['type'];

/** Horizontal or vertical merge between adjacent Text or Number table cells. */
export type TableCellMerge = api.DocumentTableCellMerge;

/** Mapping of records in a table */
export interface TableRecords {
    [key: string]: TableRecord;
}

/** Mapping of definitions in a table */
export interface TableDefinitions {
    [key: string]: TableColumnDefinition;
}

/**
 * Mapping of changes in a table.
 */
export type TableDiff = {
    recordValues: Map<
        // record key
        string,
        Map<
            // column key
            string,
            {
                before:
                    | {
                          definition: TableColumnDefinition;
                          value: TableRecordValue;
                      }
                    | undefined;
                after: TableRecordValue;
            }
        >
    >;
    definitions: Map<
        // column key
        string,
        {
            before: TableColumnDefinition | undefined;
            after: TableColumnDefinition;
        }
    >;
    view: {
        // For table view
        hideHeader?: {
            before: boolean;
            after: boolean;
        };
        stickyHeader?: {
            before: boolean;
            after: boolean;
        };
        stickyFirstColumn?: {
            before: boolean;
            after: boolean;
        };
        cellMerges?: {
            before: TableCellMerge[] | undefined;
            after: TableCellMerge[] | undefined;
        };
        // For cards view
        hideColumnTitle?: {
            before: boolean;
            after: boolean;
        };
    };
};

/** Data for Tables. */
export interface BlockTableData {
    records: TableRecords;
    definition: TableDefinitions;
    view: TableView;
    /** Horizontal or vertical merges between adjacent Text or Number body cells. */
    cellMerges?: TableCellMerge[];
    /** Whether to render the block as a full width one */
    fullWidth?: boolean;
    /**
     * Whether to show the search bar on the table.
     * When `undefined`, the renderer falls back to its smart default (search is shown on grid
     * tables once they have enough rows). `true`/`false` force search on or off, overriding that
     * default — e.g. enabling search on cards or small tables, or disabling it on large ones.
     */
    search?: boolean;
    /**
     * Computed diff of the table block.
     * This is used to display changes in the table.
     * It is a map of record keys to a map of column keys to the before and after values.
     */
    diff?: TableDiff;
}

//
// Mention content-ref
//
export const BLOCK_CONTENT_REF = 'content-ref';
export interface BlockContentRefData {
    ref: api.ContentRef;
}

//
// Reusable content
//
export const BLOCK_REUSABLE_CONTENT: api.DocumentBlockReusableContent['type'] = 'reusable-content';
export type BlockReusableContentData = api.DocumentBlockReusableContent['data'];

//
// Custom blocks from integrations
//
export const BLOCK_INTEGRATION = 'integration';
export type BlockIntegrationData = api.DocumentBlockIntegration['data'];

//
// Marks
//
export const MARK_BOLD: api.DocumentMarkBold['type'] = 'bold';
export const MARK_ITALIC: api.DocumentMarkItalic['type'] = 'italic';
export const MARK_STRIKETHROUGH: api.DocumentMarkStrikethrough['type'] = 'strikethrough';
export const MARK_CODE: api.DocumentMarkCode['type'] = 'code';
export const MARK_COLOR: api.DocumentMarkColor['type'] = 'color';
export const MARK_KEYBOARD: api.DocumentMarkKeyboard['type'] = 'keyboard';
export const MARK_SUPERSCRIPT: api.DocumentMarkSuperscript['type'] = 'superscript';
export const MARK_SUBSCRIPT: api.DocumentMarkSubscript['type'] = 'subscript';

/**
 * Every persisted mark color string (text + background). Must match the OpenAPI
 * `DocumentTextColor` / `DocumentTextBackgroundColor` enums (same list), the
 * palette keys / `$*` tokens from `ContentEditorColorContext`, and hive `TagColor` /
 * `VALID_TAG_COLORS` so HTML/Markdown round-trip and tags stay valid.
 */
export const MARK_COLOR_VALUES = arrayOfAll<
    api.DocumentTextColor | api.DocumentTextBackgroundColor
>()([
    api.DocumentTextColor.Default,
    api.DocumentTextColor.Green,
    api.DocumentTextColor.Blue,
    api.DocumentTextColor.Red,
    api.DocumentTextColor.Orange,
    api.DocumentTextColor.Yellow,
    api.DocumentTextColor.Purple,
    api.DocumentTextColor.Pink,
    api.DocumentTextColor.Violet,
    api.DocumentTextColor.Cyan,
    api.DocumentTextColor.Primary,
    api.DocumentTextColor.Secondary,
    api.DocumentTextColor.Info,
    api.DocumentTextColor.Success,
    api.DocumentTextColor.Warning,
    api.DocumentTextColor.Danger,
    api.DocumentTextColor.Tint,
]);
export type ContentColor = (typeof MARK_COLOR_VALUES)[number];

export type MarkColorData = Partial<api.DocumentMarkColor['data']>;

//
// Inlines
//
export const INLINE_LINK = 'link';
export type InlineLinkData = api.DocumentInlineLink['data'];

export const INLINE_BUTTON = 'button';
export type InlineButtonData = api.DocumentInlineButton['data'];
export type InlineButtonSize = NonNullable<api.DocumentInlineButton['data']['size']>;
// One list so serialize/parse/UI don't each hardcode the set.
export const INLINE_BUTTON_SIZES: InlineButtonSize[] = ['small', 'medium', 'large'];
// The absent value: never persisted or serialized, so "no size" means one thing everywhere.
export const DEFAULT_INLINE_BUTTON_SIZE: InlineButtonSize = 'large';

export const INLINE_EMOJI = 'emoji';
export type InlineEmojiData = api.DocumentInlineEmoji['data'];

export const INLINE_ICON = 'icon';
export type InlineIconData = api.DocumentInlineIcon['data'];

export const INLINE_EXPRESSION = 'expression';
export type InlineExpressionData = api.DocumentInlineExpression['data'];

export const INLINE_MENTION = 'mention';
export type InlineMentionData = api.DocumentInlineMention['data'];

export const INLINE_IMAGE = 'inline-image';

export type InlineImageSize = NonNullable<api.DocumentInlineImage['data']['size']>;
export type InlineImageData = api.DocumentInlineImage['data'];

export const INLINE_ANNOTATION = 'annotation';

export const FRAGMENT_ANNOTATION_BODY = 'annotation-body';

//
// Diffs
//

export enum BlockDiffChangeType {
    Added = 'added',
    Deleted = 'deleted',
    Modified = 'modified',
    ModifiedInside = 'modified_inside',
}

export const BLOCK_DIFF = 'diff-block';
export const BLOCK_DIFF_UNMODIFIED = 'diff-unmodified';
export interface BlockDiffData {
    type: BlockDiffChangeType;
}

export const MARK_DIFF_ADDED = 'diff-added';
export const MARK_DIFF_DELETED = 'diff-deleted';
export const MARK_DIFF_MODIFIED = 'diff-modified';
export const MARK_DIFF_SET = new Set([MARK_DIFF_ADDED, MARK_DIFF_DELETED, MARK_DIFF_MODIFIED]);

//
// Others
//

// Leaf blocks that contains rich texts
export const LEAF_RICH_BLOCKS = [
    BLOCK_PARAGRAPH,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
];
export const LEAF_TEXT_BLOCKS = [...LEAF_RICH_BLOCKS, BLOCK_CODE_LINE];
export const LEAF_TEXT_BLOCKS_SET = new Set(LEAF_TEXT_BLOCKS);

// Blocks that are voids
export const VOID_BLOCKS = [BLOCK_DIVIDER, BLOCK_FILE, BLOCK_EMBED, BLOCK_DRAWING];

// Leaf blocks (don't contain blocks)
export const LEAF_BLOCKS = [...LEAF_TEXT_BLOCKS, ...VOID_BLOCKS];

// Blocks that are lists
export const LIST_BLOCKS_ARRAY = [BLOCK_LIST_UL, BLOCK_LIST_OL, BLOCK_LIST_TASKS] as const;
export const LIST_BLOCKS: string[] = [...LIST_BLOCKS_ARRAY];

/**
 * List of all valid blocks.
 *
 * `as const` so that {@link BLOCK_TYPES} is the union of the block types instead of `string`: code
 * that has to handle every block relies on a missing one being a compilation error.
 */
export const BLOCKS_ARRAY = [
    BLOCK_PARAGRAPH,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_FILE,
    BLOCK_EMBED,
    BLOCK_DRAWING,
    BLOCK_CODE,
    BLOCK_CODE_LINE,
    BLOCK_COLUMNS,
    BLOCK_COLUMN,
    BLOCK_LIST_UL,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_ITEM,
    BLOCK_TABLE,
    BLOCK_IMAGES,
    BLOCK_IMAGE,
    BLOCK_MATH,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_UPDATES,
    BLOCK_UPDATE,
    BLOCK_HINT,
    BLOCK_PROMPT,
    BLOCK_QUOTE,
    BLOCK_IF,
    BLOCK_CONTENT_REF,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_EXPANDABLE,
    BLOCK_INTEGRATION,
    BLOCK_DIVIDER,
    BLOCK_REUSABLE_CONTENT,
] as const;

export const BLOCKS: Set<string> = new Set(BLOCKS_ARRAY);

/**
 * List of all valid inlines.
 */
export const INLINES = new Set([
    INLINE_LINK,
    INLINE_BUTTON,
    INLINE_IMAGE,
    INLINE_MATH,
    INLINE_EMOJI,
    INLINE_ICON,
    INLINE_EXPRESSION,
    INLINE_MENTION,
    INLINE_ANNOTATION,
] as const);

export type INLINE_TYPES = typeof INLINES extends Set<infer I> ? I : never;
export type BLOCK_TYPES = (typeof BLOCKS_ARRAY)[number];

/**
 * List of all valid marks in a document (non-diff)
 */
export const MARKS = new Set([
    MARK_BOLD,
    MARK_ITALIC,
    MARK_STRIKETHROUGH,
    MARK_SUPERSCRIPT,
    MARK_SUBSCRIPT,
    MARK_CODE,
    MARK_COLOR,
    MARK_KEYBOARD,
]);
